import { startCase } from "lodash";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import { useDispatch, useSelector } from "react-redux";
import { ThunkDispatch } from "@reduxjs/toolkit";
import { AnyAction } from "redux";
import {
  getAllVpfRequests,
  createOrUpdateVpfRequest,
  clearError,
} from "../../../../core/data/redux/vpfSlice";
import Table from "../../../../core/common/dataTable/index";
import { all_routes } from "../../../router/all_routes";

type AppDispatch = ThunkDispatch<any, any, AnyAction>;

const VPFContributionModule: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { data: vpfRequests, loading, createLoading, total } = useSelector(
    (state: any) => state.vpfState
  );

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isTermsModalOpen, setIsTermsModalOpen] = useState(false);

  // Get user data from localStorage
  const user: any = localStorage.getItem("user");
  const userJson = user && user !== "undefined" ? JSON.parse(user) : {};
  const [currentVpfAmount] = useState(userJson?.details?.vPFAmount || '0.00');
  const [payscale] = useState(userJson?.details?.pay_scale || '');

  const [DeductionFromTheMonth, setDeductionFromTheMonth] = useState('');
  const [DeductionFromTheYear, setDeductionFromTheYear] = useState('');
  const [VpfAmount, setVpfAmount] = useState('0.00');
  const [VpfAcknowledge, setVpfAcknowledge] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");

  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });

  // Fetch VPF requests on component mount
  useEffect(() => {
    fetchVpfRequests();

    return () => {
      dispatch(clearError());
    };
  }, []);

  // Update pagination total when data changes
  useEffect(() => {
    setPagination((prev) => ({
      ...prev,
      total: total,
    }));
  }, [total]);

  const fetchVpfRequests = (params = {}) => {
    const payload = {
      page: pagination.current,
      limit: pagination.pageSize,
      query: searchQuery,
      ...params,
    };

    console.log("Fetching VPF requests with payload:", payload);

    (dispatch as any)(getAllVpfRequests(payload))
      .unwrap()
      .then((response: any) => {
        console.log("VPF List Response:", response);
      })
      .catch((error: any) => {
        console.error("Error fetching VPF requests:", error);
        toast.error(error || "Failed to fetch VPF requests");
      });
  };

  const handleCreateRequest = () => {
    setIsModalOpen(true);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    // Form validation
    if (!DeductionFromTheMonth) {
      toast.error('Please select deduction month');
      return;
    }
    if (!DeductionFromTheYear) {
      toast.error('Please select deduction year');
      return;
    }
    if (!VpfAmount) {
      toast.error('Please enter new/revised VPF amount');
      return;
    }
    if (!VpfAcknowledge) {
      toast.error('Please acknowledge and accept the terms & conditions');
      return;
    }

    const payload = {
      Action: "V", // As per requirement, always pass "V"
      VpfAmount: VpfAmount,
      DeductionFromTheMonth: DeductionFromTheMonth,
      DeductionFromTheYear: DeductionFromTheYear,
      VpfAcknowledge: VpfAcknowledge ? "X" : "", // Pass "X" if checked
      is_draft: false,
    };

    console.log("Creating VPF request with payload:", payload);

    (dispatch as any)(createOrUpdateVpfRequest(payload))
      .unwrap()
      .then((response: any) => {
        console.log("Create VPF Response:", response);
        toast.success(response?.message || "VPF contribution request created successfully");
        setIsModalOpen(false);
        // Reset form
        setDeductionFromTheMonth('');
        setDeductionFromTheYear('');
        setVpfAmount('0.00');
        setVpfAcknowledge(false);
        // Refresh the list
        fetchVpfRequests();
      })
      .catch((error: any) => {
        console.error("Error creating VPF request:", error);
        toast.error(error || "Failed to create VPF request");
      });
  };

  const handlePagination = (newPagination: any) => {
    setPagination((prev) => {
      const nextPageSize = newPagination.pageSize || prev.pageSize || 10;
      const nextCurrent =
        newPagination.pageSize && newPagination.pageSize !== prev.pageSize
          ? 1
          : newPagination.current || 1;

      const updatedPagination = {
        current: nextCurrent,
        pageSize: nextPageSize,
        total: newPagination.total || prev.total || 0,
      };

      // Fetch data with new pagination
      fetchVpfRequests({
        page: updatedPagination.current,
        limit: updatedPagination.pageSize,
      });

      return updatedPagination;
    });
  };

  const handleSearch = (query: string) => {
    setSearchQuery(query);
    setPagination((prev) => ({
      ...prev,
      current: 1, // Reset to first page on search
    }));
    fetchVpfRequests({
      query: query,
      page: 1,
      limit: pagination.pageSize,
    });
  };

  // Consistent Date Format: DD-MM-YYYY
  const formatDate = (dateString: string | null) => {
    if (!dateString) return "-";
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return "-";
      const day = date.getDate().toString().padStart(2, "0");
      const month = (date.getMonth() + 1).toString().padStart(2, "0");
      const year = date.getFullYear();
      return `${day}-${month}-${year}`;
    } catch (e) {
      return "-";
    }
  };

  const getStatusBadgeClass = (status: string) => {
    if (!status) return "badge-secondary";

    const lowerStatus = status.toLowerCase();

    if (lowerStatus.includes("pending") || lowerStatus.includes("to be approved")) {
      return "badge-warning";
    } else if (lowerStatus.includes("approved") || lowerStatus === "a") {
      return "badge-success";
    } else if (lowerStatus.includes("reject") || lowerStatus === "r") {
      return "badge-danger";
    } else if (lowerStatus.includes("progress") || lowerStatus.includes("insapprocess")) {
      return "badge-info";
    } else {
      return "badge-secondary";
    }
  };

  // Columns definition
  const columns = [
    {
      title: "Sr. No.",
      dataIndex: "id",
      render: (text: string, record: any, index: number) => (
        <div>
          {pagination.current
            ? index + pagination.current * pagination.pageSize - pagination.pageSize + 1
            : index + 1}
        </div>
      ),
    },
    {
      title: "Reference Number",
      dataIndex: "ReferenceNumber",
      sorter: (a: any, b: any) =>
        (a.ReferenceNumber || "").localeCompare(b.ReferenceNumber || ""),
      render: (text: string) => (
        <span className="fw-medium">{text || "-"}</span>
      ),
    },

    {
      title: "Application Status",
      dataIndex: "Status",
      render: (status: string, record: any) => {
        const currentStatus = status || record.RequestStatus || "Pending";
        const displayStatus = currentStatus === "T" ? "To Be Approved" : currentStatus === "A" ? "Approved" : currentStatus === "R" ? "Rejected" : currentStatus;
        const badgeClass = getStatusBadgeClass(displayStatus);
        return (
          <span className={`badge ${badgeClass}`}>
            {startCase(displayStatus)}
          </span>
        );
      },
    },
    {
      title: "Amount (₹)",
      dataIndex: "VpfAmount",
      sorter: (a: any, b: any) =>
        parseFloat(a.VpfAmount || 0) - parseFloat(b.VpfAmount || 0),
      render: (text: string) => (
        <span className="fw-medium">{parseFloat(text || "0").toFixed(2)}</span>
      ),
    },
    {
      title: "Month",
      dataIndex: "DeductionFromTheMonth",
      sorter: (a: any, b: any) =>
        (a.DeductionFromTheMonth || "").localeCompare(b.DeductionFromTheMonth || ""),
      render: (text: string) => {
        if (!text) return "-";
        const monthNumber = parseInt(text);
        const monthNames = [
          "January", "February", "March", "April", "May", "June",
          "July", "August", "September", "October", "November", "December"
        ];
        return monthNames[monthNumber - 1] || text;
      },
    },
    {
      title: "Year",
      dataIndex: "DeductionFromTheYear",
      sorter: (a: any, b: any) =>
        (a.DeductionFromTheYear || "").localeCompare(b.DeductionFromTheYear || ""),
      render: (text: string) => text || "-",
    },
    {
      title: "Approver Name",
      dataIndex: "ApproverName",
      sorter: (a: any, b: any) =>
        (a.ApproverName || "").localeCompare(b.ApproverName || ""),
      render: (text: string, record: any) => {
        if (!text && !record.ApproverUserID) return "-";
        return `${text || ""} ${record.ApproverUserID ? `(${record.ApproverUserID})` : ""}`.trim();
      },
    },
    {
      title: "Req Creation Date",
      dataIndex: "RequestCreationDate",
      sorter: (a: any, b: any) =>
        new Date(a.RequestCreationDate || 0).getTime() - new Date(b.RequestCreationDate || 0).getTime(),
      render: (text: string) => formatDate(text),
    },
    {
      title: "Action",
      dataIndex: "action",
      render: (_: any, record: any) => (
        <Link
          to={all_routes.vpfModule.details.replace(":id", record.id || record.ReferenceNumber || "0")}
          state={{ vpfData: record }}
          className="btn btn-sm btn-outline-primary"
        >
          <i className="ti ti-eye"></i> View
        </Link>
      ),
    },
  ];

  return (
    <div className="page-wrapper">
      <div className="content">
        <div className="d-md-flex d-block align-items-center justify-content-between mb-3">
          <div className="my-auto mb-2">
            <h3 className="page-title mb-1">VPF Contribution Module</h3>
            <nav>
              <ol className="breadcrumb mb-0">
                <li className="breadcrumb-item">
                  <Link to={all_routes.employeeDashboard}>Dashboard</Link>
                </li>
                <li className="breadcrumb-item">
                  <Link to="#">Services</Link>
                </li>
                <li className="breadcrumb-item active" aria-current="page">
                  VPF Contribution
                </li>
              </ol>
            </nav>
          </div>
        </div>

        <div className="card">
          <div className="card-header d-flex align-items-center justify-content-between flex-wrap pb-0">
            <h4 className="mb-4 mt-3">Previous Requests</h4>
            <div className="d-flex align-items-center gap-2">
              <Link
                to="#"
                className="btn btn-primary"
                onClick={(e) => {
                  e.preventDefault();
                  handleCreateRequest();
                }}
              >
                <i className="ti ti-plus me-1"></i>
                Create Request
              </Link>
            </div>
          </div>

          <div className="card-body p-0 py-3">
            <Table
              columns={columns}
              dataSource={vpfRequests?.map((item: any, idx: number) => ({ ...item, key: item.id || item.ReferenceNumber || idx }))}
              Selection={false}
              paginationConfig={pagination}
              handlePagination={handlePagination}
              loading={loading}
              handleSearch={handleSearch}
            />
          </div>
        </div>

        {/* Create Request Modal */}
        {isModalOpen && (
          <div
            className="modal fade show d-block"
            tabIndex={-1}
            style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}
          >
            <div className="modal-dialog modal-dialog-centered modal-lg">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">
                    <i className="ti ti-coin me-2"></i>
                    Create VPF Contribution Request
                  </h5>
                  <button
                    type="button"
                    className="btn-close"
                    onClick={() => setIsModalOpen(false)}
                    aria-label="Close"
                  ></button>
                </div>
                <form onSubmit={handleSubmit}>
                  <div className="modal-body">
                    {/* Instructions Section */}
                    <div className="alert alert-warning mb-4">
                      <h6 className="alert-heading mb-2">
                        <strong>Instructions:</strong>
                      </h6>
                      <ul className="mb-0 ps-3">
                        <li className="mb-1">
                          Please enter month and year from which the new VPF amount deduction will be started
                        </li>
                        <li className="mb-0">
                          To stop VPF contribution, please enter 'New/Revised VPF amount' as 0.00
                        </li>
                      </ul>
                    </div>

                    {/* Form Fields */}
                    <div className="row">
                      <div className="col-md-6 mb-3">
                        <label className="form-label">
                          Current VPF Amount (₹)
                        </label>
                        <input
                          type="text"
                          className="form-control"
                          value={currentVpfAmount}
                          readOnly
                          disabled
                          style={{ backgroundColor: '#f8f9fa' }}
                        />
                      </div>

                      <div className="col-md-6 mb-3">
                        <label className="form-label">
                          Payscale
                        </label>
                        <input
                          type="text"
                          className="form-control"
                          value={payscale}
                          readOnly
                          disabled
                          style={{ backgroundColor: '#f8f9fa' }}
                        />
                      </div>
                    </div>

                    <div className="row">
                      <div className="col-md-6 mb-3">
                        <label className="form-label">
                          <span className="text-danger">*</span> Deduction to be started from month
                        </label>
                        <select
                          className="form-select"
                          value={DeductionFromTheMonth}
                          onChange={(e) => setDeductionFromTheMonth(e.target.value)}
                          required
                        >
                          <option value="">Select Month</option>
                          <option value="01">January</option>
                          <option value="02">February</option>
                          <option value="03">March</option>
                          <option value="04">April</option>
                          <option value="05">May</option>
                          <option value="06">June</option>
                          <option value="07">July</option>
                          <option value="08">August</option>
                          <option value="09">September</option>
                          <option value="10">October</option>
                          <option value="11">November</option>
                          <option value="12">December</option>
                        </select>
                      </div>

                      <div className="col-md-6 mb-3">
                        <label className="form-label">
                          <span className="text-danger">*</span> and Year
                        </label>
                        <select
                          className="form-select"
                          value={DeductionFromTheYear}
                          onChange={(e) => setDeductionFromTheYear(e.target.value)}
                          required
                        >
                          <option value="">Select Year</option>
                          {[...Array(5)].map((_, i) => {
                            const year = new Date().getFullYear() + i;
                            return <option key={year} value={year}>{year}</option>;
                          })}
                        </select>
                      </div>
                    </div>

                    <div className="row">
                      <div className="col-md-12 mb-3">
                        <label className="form-label">
                          Please enter New/Revised VPF amount to be deducted
                        </label>
                        <input
                          type="number"
                          className="form-control"
                          value={VpfAmount}
                          onChange={(e) => setVpfAmount(e.target.value)}
                          placeholder="0.00"
                          step="0.01"
                          min="0"
                        />
                      </div>
                    </div>

                    <div className="row">
                      <div className="col-md-12 mb-3">
                        <div className="form-check">
                          <input
                            className="form-check-input"
                            type="checkbox"
                            id="VpfAcknowledge"
                            checked={VpfAcknowledge}
                            onChange={(e) => setVpfAcknowledge(e.target.checked)}
                            required
                          />
                          <label className="form-check-label" htmlFor="VpfAcknowledge">
                            <span className="text-danger">*</span> Acknowledge & accept{' '}
                            <a href="#" className="text-primary" onClick={(e) => { e.preventDefault(); setIsTermsModalOpen(true); }}>Term & conditions</a>
                          </label>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="modal-footer d-flex justify-content-between border-none" style={{ borderTop: 'none' }}>
                    <button
                      type="button"
                      className="btn btn-light"
                      onClick={() => setIsModalOpen(false)}
                      disabled={createLoading}
                    >
                      <i className="ti ti-x me-2"></i>
                      Cancel
                    </button>
                    <button type="submit" className="btn btn-primary" disabled={createLoading}>
                      {createLoading ? (
                        <>
                          <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                          Submitting...
                        </>
                      ) : (
                        <>
                          <i className="ti ti-check me-2"></i>
                          Submit Request
                        </>
                      )}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}

        {/* Terms and Conditions Modal */}
        {isTermsModalOpen && (
          <div
            className="modal fade show d-block"
            tabIndex={-1}
            style={{ backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1060 }}
          >
            <div className="modal-dialog modal-dialog-centered">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">Term and Conditions</h5>
                  <button
                    type="button"
                    className="btn-close"
                    onClick={() => setIsTermsModalOpen(false)}
                    aria-label="Close"
                  ></button>
                </div>
                <div className="modal-body">
                  <p className="mb-0">
                    I hereby declare that I will be abide by the Rules & regulations of the Company.
                    If any discrepancies found, I am liable to be dealt as per the Law
                  </p>
                </div>
                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={() => setIsTermsModalOpen(false)}
                  >
                    OK
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default VPFContributionModule;
